import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';
import { WordCloudController, WordElement } from 'chartjs-chart-wordcloud';
import Skeleton from '@mui/material/Skeleton';
import { Typography } from '@components/Typography';
import { CloudBox } from './styles';

ChartJS.register(WordCloudController, WordElement);

const POSITIVE_RGB = '76, 196, 116';
const NEGATIVE_RGB = '255, 88, 83';
const POSITIVE_HOVER_RGB = '40, 140, 70';
const NEGATIVE_HOVER_RGB = '190, 45, 40';

const KeywordCloud = ({ words, title, type, loading, onClickWord }) => {
    const baseRgb = type === 'positive' ? POSITIVE_RGB : NEGATIVE_RGB;
    const hoverRgb = type === 'positive' ? POSITIVE_HOVER_RGB : NEGATIVE_HOVER_RGB;

    const chartData = useMemo(() => {
        if (!words.length) return null;

        const maxValue = Math.max(...words.map(w => w.value));
        const minValue = Math.min(...words.map(w => w.value));
        const range = maxValue - minValue || 1;

        return {
            labels: words.map(w => w.text),
            datasets: [
                {
                    label: '',
                    fit: true,
                    data: words.map(w => Math.round(12 + ((w.value - minValue) / range) * 18)),
                    color: words.map(w => {
                        const ratio = (w.value - minValue) / range;
                        return `rgba(${baseRgb}, ${(0.5 + ratio * 0.5).toFixed(2)})`;
                    }),
                    hoverColor: words.map(() => `rgba(${hoverRgb}, 1)`),
                },
            ],
        };
    }, [words, baseRgb, hoverRgb]);

    const chartOptions = useMemo(
        () => ({
            font: { family: '"Roboto","Helvetica","Arial",sans-serif' },
            layout: { padding: 24 },
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
            },
            elements: {
                word: {
                    minRotation: 0,
                    maxRotation: 0,
                },
            },
            onClick: (_, elements) => {
                if (elements.length > 0 && onClickWord) {
                    onClickWord(words[elements[0].index].text);
                }
            },
            maintainAspectRatio: false,
        }),
        [words, onClickWord],
    );

    return (
        <CloudBox>
            <Typography variant="title1" color="textPrimary" style={{ marginBottom: '16px', display: 'block' }}>
                {title}
            </Typography>
            {loading ? (
                <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: 1 }} />
            ) : (
                words.length > 0 && (
                    <div style={{ flex: 1, position: 'relative', cursor: 'pointer' }}>
                        <Chart type="wordCloud" data={chartData} options={chartOptions} />
                    </div>
                )
            )}
        </CloudBox>
    );
};

export default KeywordCloud;
